import { c as _c } from "react/compiler-runtime";
import { getOrgPlaybooksEditSessionsByEditSessionId, postOrgPlaybooksEditSessionsByEditSessionIdRun } from '@onefootprint/fetch-dashboard';
import { onboarding } from '@onefootprint/footprint-js';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoCirclePlay16, IcoInfo16 } from '@onefootprint/icons';
import type { ApiNode } from '@onefootprint/request-types/dashboard';
import { Button, InlineAlert, Portal, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useHiddenNodeErrors from '@/components/playbook-builder/hooks/use-hidden-node-errors';
import useStore from '@/components/playbook-builder/hooks/use-store';
import useSession from '@/hooks/use-session';
import { ERRORS_WARNINGS_DISPLAY_ID } from '../../../errors-warnings-display/errors-warnings-display';
type TestRunNodeProps = {
  node: ApiNode;
  disabled: boolean;
};
export const RunNodeTest = t0 => {
  const $ = _c(45);
  const {
    node,
    disabled
  } = t0;
  const {
    data: t1,
    visibleBanners,
    getOrCreateEditSession,
    setGraphUpdate
  } = useStore();
  const {
    graph,
    obcId
  } = t1;
  const {
    hasHiddenErrors,
    reveal: revealHiddenErrors
  } = useHiddenNodeErrors();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "perNodeTest"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  const confirm = useConfirmationDialog();
  const {
    isLive
  } = useSession();
  const showRequestErrorToast = useRequestErrorToast();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      mutationFn: _temp
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const getGraphMutation = useMutation(t3);
  let t4;
  if ($[2] !== getGraphMutation || $[3] !== getOrCreateEditSession || $[4] !== node || $[5] !== setGraphUpdate) {
    t4 = async () => {
      const editSession = await getOrCreateEditSession();
      const {
        data
      } = await postOrgPlaybooksEditSessionsByEditSessionIdRun({
        path: {
          editSessionId: editSession.id
        },
        body: {
          untilNodeId: node.id
        }
      });
      return new Promise((resolve, reject) => {
        const onClose = async () => {
          const {
            data: data_0
          } = await getGraphMutation.mutateAsync(editSession.id);
          setGraphUpdate(data_0);
          resolve(void 0);
        };
        onboarding.initialize({
          onboardingSessionToken: data.token,
          onClose,
          onComplete: onClose,
          onError: reject
        });
      });
    };
    $[2] = getGraphMutation;
    $[3] = getOrCreateEditSession;
    $[4] = node;
    $[5] = setGraphUpdate;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const runTest = t4;
  let t5;
  if ($[7] !== runTest || $[8] !== showRequestErrorToast) {
    t5 = {
      mutationFn: runTest,
      onError: showRequestErrorToast
    };
    $[7] = runTest;
    $[8] = showRequestErrorToast;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const runTestMutation = useMutation(t5);
  let t6;
  if ($[10] !== confirm || $[11] !== node || $[12] !== runTestMutation || $[13] !== t) {
    t6 = () => {
      if (!node.context.isConditional) {
        return runTestMutation.mutateAsync(undefined);
      }
      confirm.open({
        title: t(_temp2),
        description: t(_temp3),
        variant: "default",
        primaryButton: {
          label: t(_temp4),
          onClick: () => {
            runTestMutation.mutateAsync(undefined);
          }
        },
        secondaryButton: {
          label: t(_temp5)
        }
      });
    };
    $[10] = confirm;
    $[11] = node;
    $[12] = runTestMutation;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleRunTest = t6;
  if (graph?.subject !== "person" || isLive || !obcId) {
    return null;
  }
  const testEntries = getGraphMutation.data?.data?.testEntries;
  let t7;
  if ($[15] !== node || $[16] !== testEntries) {
    t7 = testEntries?.length && !testEntries?.some(entry => entry.decisionNodeId === node.id);
    $[15] = node;
    $[16] = testEntries;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const didNotReachNodeError = t7;
  let t8;
  if ($[18] !== hasHiddenErrors || $[19] !== t || $[20] !== visibleBanners) {
    t8 = !hasHiddenErrors && visibleBanners.some(_temp6) ? t(_temp7) : undefined;
    $[18] = hasHiddenErrors;
    $[19] = t;
    $[20] = visibleBanners;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const disabledTooltipText = t8;
  const t9 = !disabledTooltipText;
  const t10 = runTestMutation.isPending;
  const t11 = runTestMutation.isPending || disabled || !!disabledTooltipText;
  const t12 = hasHiddenErrors ? revealHiddenErrors : handleRunTest;
  let t13;
  if ($[22] !== t) {
    t13 = t(_temp8);
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== runTestMutation.isPending || $[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <Button prefixIcon={IcoCirclePlay16} loading={t10} disabled={t11} variant="secondary" onClick={t12} size="compact">{t13}</Button>;
    $[24] = runTestMutation.isPending;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== disabledTooltipText || $[30] !== t14 || $[31] !== t9) {
    t15 = <Tooltip side="bottom" content={disabledTooltipText} disabled={t9}>{t14}</Tooltip>;
    $[29] = disabledTooltipText;
    $[30] = t14;
    $[31] = t9;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t(_temp9);
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <IcoInfo16 />;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16) {
    t18 = <Tooltip side="bottom" content={t16}>{t17}</Tooltip>;
    $[36] = t16;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== didNotReachNodeError || $[39] !== t) {
    t19 = didNotReachNodeError ? <Portal selector={`#${ERRORS_WARNINGS_DISPLAY_ID}`}><InlineAlert variant="warning"><div className="text-body-2 text-warning">{t(_temp10)}</div></InlineAlert></Portal> : null;
    $[38] = didNotReachNodeError;
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t15 || $[42] !== t18 || $[43] !== t19) {
    t20 = <div className="flex flex-row items-center gap-2">{t15}{t18}{t19}</div>;
    $[41] = t15;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
};
async function _temp(editSessionId) {
  return await getOrgPlaybooksEditSessionsByEditSessionId({
    path: {
      editSessionId
    }
  });
}
function _temp2($) {
  return $.conditionalNodeConfirm.title;
}
function _temp3($_0) {
  return $_0.conditionalNodeConfirm.description;
}
function _temp4($_1) {
  return $_1.conditionalNodeConfirm.confirm;
}
function _temp5($_2) {
  return $_2.conditionalNodeConfirm.cancel;
}
function _temp6(b) {
  return b.type === "error";
}
function _temp7($_3) {
  return $_3.tooltipHasErrors;
}
function _temp8($_4) {
  return $_4.title;
}
function _temp9($_5) {
  return $_5.tooltipDescription;
}
function _temp10($_6) {
  return $_6.didNotReachNodeError;
}